import { MarketplacePaymentsDetails } from 'src/modals/MaketplacePayments';
import { ModalProps } from 'src/modals/Modal';
import { MainRoute } from 'src/navigations/Routes/MainRoute';
import { MarketplacePaymentIdDeeplinkQueryParam } from 'src/utils/constants/constants';
import { getPathFromMainRoute } from 'src/utils/deepLinkUtils';
import { tryAddMoneySameCurrency } from 'src/utils/ticketMetricUtils';
import {
  Marketplace,
  MarketplacePaymentClient,
  MarketplacePaymentState,
  MarketplaceSalePayment,
} from 'src/WebApiController';

export const getMarketplacePaymentRelativeUrl = (
  marketplacePaymentId: number
) => {
  return `${getPathFromMainRoute(
    MainRoute.Payments
  )}?${MarketplacePaymentIdDeeplinkQueryParam}=${marketplacePaymentId}`;
};

export const getMarketplacePaymentDetailsModalConfigWithDeepLink = (
  marketplacePaymentId: number
): ModalProps => {
  return {
    ...MarketplacePaymentsDetails,
    deepLinkValue: marketplacePaymentId,
  };
};

export const getIsMarketplacePaymentLockedByExternalPaymentId = async (
  client: MarketplacePaymentClient,
  externalPaymentId: string,
  marketplace: Marketplace | null
) => {
  return await client.getIsMarketplacePaymentLockedByExternalPaymentId(
    externalPaymentId,
    marketplace
  );
};

export const createAggregatedMarketplaceSalePayment = (
  allMsps: MarketplaceSalePayment[] | null | undefined
): MarketplaceSalePayment | null => {
  if (!allMsps?.length) {
    return null;
  }

  const firstPayment = allMsps[0];

  const aggregatedPmtAmt = tryAddMoneySameCurrency(
    allMsps,
    (msp) => msp.pmtAmt?.amt,
    (msp) => msp.pmtAmt?.currency
  );

  const aggregatedProceedsAmt = tryAddMoneySameCurrency(
    allMsps,
    (msp) => msp.proceedsAmt?.amt,
    (msp) => msp.proceedsAmt?.currency
  );

  const aggregatedCredit = tryAddMoneySameCurrency(
    allMsps,
    (msp) => msp.credit?.amt,
    (msp) => msp.credit?.currency
  );

  const aggregatedCharge = tryAddMoneySameCurrency(
    allMsps,
    (msp) => msp.charge?.amt,
    (msp) => msp.charge?.currency
  );

  const aggregatedConvertedPmtAmt = tryAddMoneySameCurrency(
    allMsps,
    (msp) => msp.convertedPmtAmt?.amt,
    (msp) => msp.convertedPmtAmt?.currency
  );

  const aggregatedManualPmtAmt = tryAddMoneySameCurrency(
    allMsps,
    (msp) => msp.manualPmtAmt?.amt,
    (msp) => msp.manualPmtAmt?.currency
  );

  const aggregatedManualProceedsAmt = tryAddMoneySameCurrency(
    allMsps,
    (msp) => msp.manualProceedsAmt?.amt,
    (msp) => msp.manualProceedsAmt?.currency
  );

  const aggregatedManualCreditAmt = tryAddMoneySameCurrency(
    allMsps,
    (msp) => msp.manualCreditAmt?.amt,
    (msp) => msp.manualCreditAmt?.currency
  );

  const aggregatedManualChargeAmt = tryAddMoneySameCurrency(
    allMsps,
    (msp) => msp.manualChargeAmt?.amt,
    (msp) => msp.manualChargeAmt?.currency
  );

  const aggregatedPayment: MarketplaceSalePayment = {
    id: Math.abs(firstPayment.id) * -1,
    mkp: firstPayment.mkp,
    extPmtId: '',
    pmtRefId: null,
    pmtDate: null,
    recvDate: null,
    state: MarketplacePaymentState.Ineligible,
    pmtAmt: aggregatedPmtAmt!,
    proceedsAmt: aggregatedProceedsAmt,
    credit: aggregatedCredit,
    charge: aggregatedCharge,
    convertedPmtAmt: aggregatedConvertedPmtAmt,
    mkpPmtTerm: null,
    manualPmtAmt: aggregatedManualPmtAmt,
    manualProceedsAmt: aggregatedManualProceedsAmt,
    manualCreditAmt: aggregatedManualCreditAmt,
    manualChargeAmt: aggregatedManualChargeAmt,
    isLocked: false,
  };

  return aggregatedPayment;
};
